import React from "react";
import styled from "styled-components";
import { Container, Section, Grid } from "../components/ui";
import SEO from "../components/SEO";
import whyy_thePulse_newCalendar from "../media/whyy_thePulse_newCalendar.mp3";

const PageHeader = styled.div`
  text-align: center;
  padding: 4rem 0;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
`;

const PageTitle = styled.h1`
  margin-bottom: 1rem;
`;

const PageDesc = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  max-width: 600px;
  margin: 0 auto;
`;

const FeaturedCard = styled.div`
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
`;

const FeaturedBadge = styled.div`
  background: #eab308;
  color: #0f0f0f;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FeaturedContent = styled.div`
  padding: 2rem;
`;

const FeaturedTitle = styled.h2`
  margin-bottom: 0.5rem;
`;

const FeaturedSource = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const FeaturedDesc = styled.p`
  color: #374151;
  line-height: 1.75;
  margin-bottom: 1.5rem;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  margin-top: 1rem;
`;

const MediaCard = styled.a`
  display: block;
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  text-decoration: none;
  transition: all 250ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
  }
`;

const MediaLogo = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  img {
    max-height: 100%;
    max-width: 150px;
    object-fit: contain;
  }
`;

const MediaTitle = styled.h3`
  color: #0f0f0f;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
`;

const MediaDesc = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div``;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #eab308;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const mediaItems = [
  {
    title: "Passage to Profit (iHeartRadio)",
    source: "Podcast",
    description: "Discussion on entrepreneurship and the journey of building The New Calendar.",
    url: "https://passagetoprofitshow.com/podcast/035-passage-to-profit-show-04-28-2019/",
    logo: require("../images/media/iheartmedia.png"),
  },
  {
    title: "NPR's The Pulse",
    source: "WHYY Philadelphia",
    description: "Time to rip up the calendar? How about 5 seasons, 9 days in a week.",
    url: "https://whyy.org/segments/time-to-rip-up-the-calendar-how-about-5-seasons-9-days-in-a-week/",
    logo: require("../images/media/thePulse.webp"),
  },
  {
    title: "ABC 6 Action News",
    source: "Philadelphia",
    description: "Local scientists say they've discovered a 5th season.",
    url: "https://6abc.com/weather/local-scientists-say-theyve-discovered-a-5th-season/5167360/",
    logo: require("../images/media/wpviActionNews.webp"),
  },
  {
    title: "Cape Gazette",
    source: "Delaware",
    description: "Is the calendar dated? Thomas Sherman thinks so.",
    url: "https://www.capegazette.com/article/calendar-dated-thomas-sherman-thinks-so/176217",
    logo: require("../images/media/cape_gazette_logo.png"),
  },
];

const Media = () => {
  return (
    <>
      <SEO
        title="Media"
        description="The New Calendar in the media. Featured on NPR's The Pulse, ABC Action News, and more."
        url="https://thenewcalendar.com/media"
      />

      <PageHeader>
        <Container>
          <PageTitle>In the Media</PageTitle>
          <PageDesc>
            See what people are saying about The New Calendar and the discovery 
            of the 5th season.
          </PageDesc>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          {/* Featured: NPR */}
          <FeaturedCard style={{ marginBottom: '3rem' }}>
            <FeaturedBadge>Featured Story</FeaturedBadge>
            <FeaturedContent>
              <FeaturedTitle>NPR's The Pulse: "Time to rip up the calendar?"</FeaturedTitle>
              <FeaturedSource>WHYY Philadelphia • NPR Member Station</FeaturedSource>
              <FeaturedDesc>
                "The Pulse tells stories at the heart of health, science and innovation. 
                In this segment, we explore a Delaware inventor's radical reimagining of 
                how we measure time—a calendar built on mathematical principles rather 
                than ancient traditions."
              </FeaturedDesc>
              <AudioPlayer controls>
                <source src={whyy_thePulse_newCalendar} type="audio/mpeg" />
                Your browser does not support the audio element.
              </AudioPlayer>
            </FeaturedContent>
          </FeaturedCard>

          {/* Other Media */}
          <h2 style={{ marginBottom: '1.5rem' }}>More Coverage</h2>
          <Grid cols={2} gap="1.5rem">
            {mediaItems.map((item, i) => (
              <MediaCard key={i} href={item.url}>
                <MediaLogo>
                  <img src={item.logo} alt={item.title} />
                </MediaLogo>
                <MediaTitle>{item.title}</MediaTitle>
                <MediaDesc>{item.description}</MediaDesc>
              </MediaCard>
            ))}
          </Grid>

          {/* Stats */}
          <StatsSection>
            <StatItem>
              <StatNumber>25K+</StatNumber>
              <StatLabel>TikTok Followers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>4</StatNumber>
              <StatLabel>Videos with 1M+ Views</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>10+</StatNumber>
              <StatLabel>Years in Development</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>5</StatNumber>
              <StatLabel>Seasons Discovered</StatLabel>
            </StatItem>
          </StatsSection>
        </Container>
      </Section>
    </>
  );
};

export default Media;
